import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  TemplateTask,
  CreateTemplateTaskSchema,
  UpdateTemplateTaskSchema,
  useCreateTemplateTask,
  useUpdateTemplateTask,
} from "@/lib/api/checklist/templates";
import { useListMinimalUsers } from "@/lib/api/users";
import { toast } from "@/components/ui/use-toast";

interface CreateEditTemplateTaskModalProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
  task?: TemplateTask;
}

export function CreateEditTemplateTaskModal({
  open,
  onClose,
  templateId,
  task,
}: CreateEditTemplateTaskModalProps) {
  const isEdit = !!task;
  const createTask = useCreateTemplateTask(templateId);
  const updateTask = useUpdateTemplateTask(templateId, task?.id || "");
  const { data: usersData } = useListMinimalUsers();
  const users = usersData?.data || [];

  const userOptions = users.map((user: { id: string; name: string }) => ({
    value: user.id,
    label: user.name,
  }));

  const form = useForm<z.infer<typeof CreateTemplateTaskSchema> | z.infer<typeof UpdateTemplateTaskSchema>>({
    resolver: zodResolver(isEdit ? UpdateTemplateTaskSchema : CreateTemplateTaskSchema),
    defaultValues: isEdit
      ? {
          name: task.title,
          description: task.description || "",
          auto_assigned_user_ids: task.auto_assigned_user_ids || [],
          due_date_offset: task.due_date_offset || { days: 0 },
        }
      : {
          name: "",
          description: "",
          auto_assigned_user_ids: [],
          due_date_offset: { days: 1 },
        },
  });

  useEffect(() => {
    if (open && task) {
      form.reset({
        name: task.title,
        description: task.description || "",
        auto_assigned_user_ids: task.auto_assigned_user_ids || [],
        due_date_offset: task.due_date_offset || { days: 0 },
      });
    } else if (open && !task) {
      form.reset({
        name: "",
        description: "",
        auto_assigned_user_ids: [],
        due_date_offset: { days: 1 },
      });
    }
  }, [open, task, form]);

  const handleSubmit = async (data: any) => {
    if (createTask.isLoading || updateTask.isLoading) return;
    try {
      if (isEdit) {
        const result = await updateTask.mutateAsync(data);
        if (!result.success) { throw result; }
        toast({
          title: "Success",
          description: "Task updated successfully",
        });
      } else {
        const result = await createTask.mutateAsync(data);
        if (!result.success) { throw result; }
        toast({
          title: "Success",
          description: "Task added to template",
        });
      }
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to ${isEdit ? "update" : "create"} task`,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Template Task" : "Add Task to Template"}
          </DialogTitle>
          <DialogDescription>
            Define a task that will be created when this template is triggered
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g., Send welcome email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describe what needs to be done..."
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="auto_assigned_user_ids"
              render={({ field }) => (
                <FormItem className="grid">
                  <FormLabel>Auto-Assign To</FormLabel>
                  <FormControl>
                    <ComboboxMulti
                      dwimOnChange
                      isModal
                      options={userOptions}
                      value={field.value || []}
                      onValueChange={field.onChange}
                      placeholder="Select users..."
                    />
                  </FormControl>
                  <FormDescription>
                    Users who will be automatically assigned when this task is created
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label>Due Date Offset</Label>
              <div className="grid grid-cols-3 gap-2">
                <FormField
                  control={form.control}
                  name="due_date_offset.days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Days</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                How long after the template is triggered should this task be due
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createTask.isLoading || updateTask.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createTask.isLoading || updateTask.isLoading}
              >
                {createTask.isLoading || updateTask.isLoading
                  ? "Saving..."
                  : isEdit
                  ? "Update Task"
                  : "Add Task"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
